import {createElement, Fragment} from 'react';
import {Box, FormControl, Grid, Typography} from '@mui/material';
import {Control, Controller} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {CareTaskFeature} from '../../../backend/enums/care-task-feature';
import {CareTaskNotDoneReason} from '../../../backend/enums/care-task-not-done-reason';
import {CareTaskData} from '../../../backend/interfaces/care-task-data';
import {
  dailyCareLogFeatureFieldTypes,
  mappedFeatureFieldsIndex,
  officeFeatureFieldTypes,
} from './feature-fields';

interface OfficeFormData {
  time: string;
  date: string;
  markedAsDone?: boolean;
  taskNotDoneReason?: CareTaskNotDoneReason;
  data?: CareTaskData;
  notes?: string;
}
interface DailyCareLogFormData {
  markedAsDone?: boolean;
  taskNotDoneReason?: CareTaskNotDoneReason;
  data?: CareTaskData;
  notes?: string;
}

interface HandleCareTaskFeatureDataFieldsProps {
  layout: 'daily-care-log' | 'office';
  feature: CareTaskFeature;
  control: Control<OfficeFormData | DailyCareLogFormData | any, any>;
  readOnly: boolean;
}

export const HandleCareTaskFeatureDataFields = ({
  layout,
  feature,
  control,
  readOnly = false,
}: HandleCareTaskFeatureDataFieldsProps) => {
  const {t} = useTranslation();

  const UIFields =
    layout === 'daily-care-log' ? dailyCareLogFeatureFieldTypes : officeFeatureFieldTypes;

  return (
    <>
      {mappedFeatureFieldsIndex[feature] && (
        <>
          {layout === 'office' && feature === CareTaskFeature.BloodPressureHealthMeasurement && (
            <FormControl>
              <Box className="label-container">
                <Typography component="label" noWrap>
                  {t(
                    translations.care_and_activities.handle_task_modal.feature_input.label[feature]
                  )}
                </Typography>
                <Typography component="span">*</Typography>
              </Box>
            </FormControl>
          )}

          <Grid container columnSpacing={2} rowSpacing={4}>
            {Object.entries(mappedFeatureFieldsIndex[feature]).map(([input, props], index) => {
              const item = mappedFeatureFieldsIndex[feature];

              const reactKey = `field_${feature}_${index}`;

              if (!item) return <Fragment key={reactKey} />;

              const gridSpacing =
                layout === 'daily-care-log' &&
                feature !== CareTaskFeature.BloodPressureHealthMeasurement
                  ? 12
                  : 12 / (Object.entries(item)?.length || 1);

              return (
                <Grid item xs={12} sm={gridSpacing} key={reactKey}>
                  <Controller
                    // @ts-ignore - type as one of any of the data fields
                    name={`data.${input}`}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t(
                          translations.care_and_activities.handle_task_modal.feature_input
                            .required_message
                        ),
                      },
                      ...item[input].rules,
                    }}
                    render={({field, fieldState: {error}}) => {
                      return createElement(UIFields[props.component], {
                        ...field,
                        ...item[input].props,
                        required: true,
                        disabled: readOnly,
                        readOnly: readOnly,
                        errors: error?.message,
                        suffix: item[input].props?.suffix,
                        label:
                          layout === 'office' && item[input].props?.label
                            ? `${item[input].props?.label} ${
                                item[input].props?.suffix ? `(${item[input].props?.suffix})` : ''
                              }`
                            : item[input].props?.label,
                      });
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </>
  );
};
